import { Component } from '@theme/component';

/**
 * A custom element that renders brand/model dropdowns for compatibility selection.
 * Delegates variant changes to the hidden native <variant-picker>.
 */
export default class CompatibilitySelector extends Component {
  /** @type {Map<string, Map<string, number>>} brand -> (model -> variantId) */
  #brandToModels = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#parseCompatibilityData();
    this.#populateBrands();
    this.#preselectFromUrl();
    // `invalid` does not bubble, so listen in capture phase on the host
    // to catch invalid events from either brandSelect or modelSelect.
    this.addEventListener('invalid', this.#onInvalid, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('invalid', this.#onInvalid, true);
  }

  // --- Data parsing ---

  #parseCompatibilityData() {
    try {
      const data = JSON.parse(this.refs.compatibilityData?.textContent || '[]');
      data.forEach(({ label, variantId }) => {
        if (!label) return;
        const parts = label.split(' / ');
        const brand = parts[0]?.trim();
        const model = parts.slice(1).join(' / ').trim();
        if (!brand) return;
        if (!this.#brandToModels.has(brand)) {
          this.#brandToModels.set(brand, new Map());
        }
        if (model) {
          this.#brandToModels.get(brand).set(model, variantId);
        }
      });
    } catch {
      // Invalid JSON — component renders without dynamic options
    }
  }

  // --- Dropdown population ---

  #populateBrands() {
    const { brandSelect } = this.refs;
    if (!brandSelect) return;

    const otherLabel = this.dataset.otherLabel || 'Other';

    Array.from(this.#brandToModels.keys())
      .sort()
      .forEach((brand) => {
        brandSelect.add(new Option(brand, brand));
      });

    brandSelect.add(new Option(otherLabel, 'other'));
  }

  #populateModels(brand) {
    const { modelSelect } = this.refs;
    if (!modelSelect) return;

    const selectModelLabel = this.dataset.selectModelLabel || 'Select model';
    const otherLabel = this.dataset.otherLabel || 'Other';
    const modelsMap = this.#brandToModels.get(brand) || new Map();

    modelSelect.innerHTML = '';
    modelSelect.add(new Option(selectModelLabel, ''));

    Array.from(modelsMap.keys())
      .sort()
      .forEach((model) => {
        const option = new Option(model, String(modelsMap.get(model)));
        option.dataset.compatLabel = `${brand} / ${model}`;
        option.dataset.model = model;
        modelSelect.add(option);
      });

    modelSelect.add(new Option(otherLabel, 'other'));
    modelSelect.disabled = false;
  }

  #resetModels() {
    const { modelSelect } = this.refs;
    if (!modelSelect) return;
    const selectModelLabel = this.dataset.selectModelLabel || 'Select model';
    modelSelect.innerHTML = '';
    modelSelect.add(new Option(selectModelLabel, ''));
    modelSelect.disabled = true;
  }

  // --- Event handlers (declarative via on:change) ---

  onBrandChange() {
    const brand = this.refs.brandSelect.value;

    this.#hideError();
    this.#setCompatibilityTitle('');
    this.#hideDisclaimer();

    if (brand === '') {
      this.#resetModels();
      this.#setIncompatibleState(false);
      this.#hideOtherMessage();
      this.#updateUrlParams({ brand: null, model: null });
      return;
    }

    if (brand === 'other') {
      this.#resetModels();
      this.#showOtherMessage();
      this.#setIncompatibleState(true);
      this.#updateUrlParams({ brand: 'other', model: null });
      return;
    }

    this.#hideOtherMessage();
    this.#setIncompatibleState(false);
    this.#populateModels(brand);
    this.#updateUrlParams({ brand, model: null });
  }

  onModelChange() {
    const { modelSelect, brandSelect } = this.refs;
    const selectedOption = modelSelect.options[modelSelect.selectedIndex];
    const brand = brandSelect.value;

    this.#hideError();

    if (modelSelect.value === 'other') {
      this.#setCompatibilityTitle('');
      this.#showOtherMessage();
      this.#setIncompatibleState(true);
      this.#hideDisclaimer();
      this.#updateUrlParams({ brand, model: 'other' });
      return;
    }

    this.#hideOtherMessage();
    this.#setIncompatibleState(false);

    if (modelSelect.value === '') {
      this.#setCompatibilityTitle('');
      this.#hideDisclaimer();
      this.#updateUrlParams({ brand, model: null });
      return;
    }

    // Set compatibility line item property
    const compatLabel = selectedOption?.dataset.compatLabel || '';
    this.#setCompatibilityTitle(compatLabel);

    const modelName = selectedOption?.dataset.model || '';

    // Update URL params BEFORE triggering variant-picker, so its
    // snapshot of window.location.href already contains brand/model.
    // The variant-picker's deferred replaceState will then preserve them.
    this.#updateUrlParams({ brand, model: modelName });

    // Delegate variant selection to hidden variant-picker
    const variantId = modelSelect.value;
    this.#selectVariant(variantId);

    // Show matching disclaimer
    this.#showDisclaimer(brand, modelName);
  }

  // --- Variant delegation ---

  /**
   * Finds the matching input in the hidden variant-picker and triggers
   * a change event on it. The variant-picker then handles:
   * - VariantSelectedEvent dispatch
   * - Section Rendering API fetch
   * - DOM morph (price, media, inventory)
   * - VariantUpdateEvent dispatch
   * - URL update (?variant=ID)
   */
  #selectVariant(variantId) {
    if (!variantId) return;

    const section = this.closest('.shopify-section');
    const variantPicker = section?.querySelector('variant-picker');
    if (!variantPicker) return;

    // Try radio input first (buttons/swatches style)
    const radioInput = variantPicker.querySelector(
      `input[data-variant-id="${variantId}"]`
    );
    if (radioInput) {
      radioInput.checked = true;
      radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Try select option (dropdown style)
    const option = variantPicker.querySelector(
      `option[data-variant-id="${variantId}"]`
    );
    if (option) {
      const select = option.closest('select');
      if (select) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  // --- URL params ---

  #updateUrlParams(updates) {
    const url = new URL(window.location.href);
    Object.entries(updates).forEach(([key, value]) => {
      if (value == null || value === '') {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
    });
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * On page load, reads ?brand= and ?model= from URL to preselect dropdowns.
   * Without explicit brand/model params the dropdowns stay empty — a bare
   * `?variant=ID` is not enough to infer the user's intended brand/model
   * because multiple brand/model pairs can map to the same variant.
   */
  #preselectFromUrl() {
    const url = new URL(window.location.href);
    const brandParam = url.searchParams.get('brand');
    const modelParam = url.searchParams.get('model');

    if (brandParam) {
      this.#preselectFromBrandModel(brandParam, modelParam);
    }
  }

  #preselectFromBrandModel(brandParam, modelParam) {
    const normalize = (s) => (s || '').trim().toLowerCase();
    const { brandSelect } = this.refs;

    // Match brand (case-insensitive)
    const brandMatch = Array.from(brandSelect.options).find(
      (opt) => normalize(opt.value) === normalize(brandParam)
    );

    if (brandParam === 'other') {
      brandSelect.value = 'other';
      this.onBrandChange();
      return;
    }

    if (!brandMatch || brandMatch.value === '') return;

    brandSelect.value = brandMatch.value;
    this.#populateModels(brandMatch.value);

    if (!modelParam) return;

    if (modelParam === 'other') {
      this.refs.modelSelect.value = 'other';
      this.onModelChange();
      return;
    }

    // Match model (case-insensitive, by data-model attribute)
    const { modelSelect } = this.refs;
    const modelMatch = Array.from(modelSelect.options).find(
      (opt) => normalize(opt.dataset?.model) === normalize(modelParam)
    );

    if (modelMatch) {
      // Select the specific <option> by reference, not by value.
      // Multiple models can share the same variantId (the option value),
      // so `modelSelect.value = ...` would pick the first matching option
      // instead of the intended one.
      modelMatch.selected = true;
      this.onModelChange();
    }
  }

  // --- Form validation ---

  /**
   * Handles the native `invalid` event from either brandSelect or modelSelect,
   * suppressing the browser's tooltip and surfacing our styled error instead.
   * Skips the error in the "other" state — `.__other-message` is the
   * user-facing message there, and the Add to Cart button is already hidden.
   */
  #onInvalid = (event) => {
    event.preventDefault();
    const { brandSelect, modelSelect } = this.refs;
    const isIncompatible =
      brandSelect?.value === 'other' || modelSelect?.value === 'other';
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!isIncompatible) {
      this.#showError(target);
    }
    target?.focus();
  };

  // --- UI helpers ---

  #showError(invalidElement) {
    this.refs.error?.removeAttribute('hidden');
    invalidElement?.setAttribute('aria-invalid', 'true');
  }

  #hideError() {
    this.refs.error?.setAttribute('hidden', '');
    this.refs.brandSelect?.setAttribute('aria-invalid', 'false');
    this.refs.modelSelect?.setAttribute('aria-invalid', 'false');
  }

  #showOtherMessage() {
    this.refs.otherMessage?.classList.remove('hidden');
  }

  #hideOtherMessage() {
    this.refs.otherMessage?.classList.add('hidden');
  }

  /**
   * Toggles the `data-incompatible` attribute (drives CSS that hides the
   * Add to Cart buttons) and sets a custom validity message so programmatic
   * form submissions are also blocked while an incompatible option is selected.
   * Validity is set on both selects because disabled elements are barred from
   * constraint validation — whichever is enabled at the moment will enforce it.
   */
  #setIncompatibleState(isIncompatible) {
    this.toggleAttribute('data-incompatible', isIncompatible);
    const message = isIncompatible ? 'incompatible' : '';
    this.refs.brandSelect?.setCustomValidity(message);
    this.refs.modelSelect?.setCustomValidity(message);
  }

  #setCompatibilityTitle(value) {
    if (this.refs.compatibilityTitle) {
      this.refs.compatibilityTitle.value = value;
    }
  }

  #showDisclaimer(brand, model) {
    this.#hideDisclaimer();
    if (!brand || !model) return;
    const fullId = `${brand} / ${model}`;
    const match = this.refs.disclaimerContainer?.querySelector(
      `[data-model="${fullId}"]`
    );
    if (match) match.hidden = false;
  }

  #hideDisclaimer() {
    this.refs.disclaimerContainer
      ?.querySelectorAll('[data-model]')
      .forEach((el) => {
        el.hidden = true;
      });
  }
}

if (!customElements.get('compatibility-selector')) {
  customElements.define('compatibility-selector', CompatibilitySelector);
}
